import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import type { ReactNode } from 'react'
import { settingsService } from '../services/settingsService'
import { auth } from '../config/firebase'
import { useAuth } from './AuthContext'

interface NavigationContextType {
  // Main sidebar state
  isSidebarOpen: boolean
  setIsSidebarOpen: (open: boolean) => void
  toggleSidebar: () => void
  
  // Submenu expansion states
  expandedSections: Record<string, boolean>
  setExpandedSections: (sections: Record<string, boolean>) => void
  toggleSection: (sectionId: string) => void
  
  // Topics sidebar state
  isTopicsSidebarCollapsed: boolean
  setIsTopicsSidebarCollapsed: (collapsed: boolean) => void
  toggleTopicsSidebar: () => void
  
  // User preferences
  userPreferences: NavigationUserPreferences
  updateUserPreference: (key: keyof NavigationUserPreferences, value: any) => void
  
  // View-specific states
  viewStates: Record<string, any>
  setViewState: (viewId: string, state: any) => void
  getViewState: (viewId: string, defaultValue?: any) => any
  
  // Utility functions
  resetAllNavigation: () => void
  exportNavigationState: () => string
  importNavigationState: (stateJson: string) => boolean
}

interface NavigationUserPreferences {
  sidebarDefaultOpen: boolean
  autoExpandFavorites: boolean
  rememberLastSection: boolean
  compactMode: boolean
  animationSpeed: 'fast' | 'normal' | 'slow'
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined)

// Local storage keys
const STORAGE_KEYS = {
  SIDEBAR_OPEN: 'nav_sidebar_open',
  EXPANDED_SECTIONS: 'nav_expanded_sections',
  TOPICS_SIDEBAR_COLLAPSED: 'nav_topics_sidebar_collapsed',
  USER_PREFERENCES: 'nav_user_preferences',
  VIEW_STATES: 'nav_view_states'
} as const

// Default expanded sections
const DEFAULT_EXPANDED_SECTIONS: Record<string, boolean> = {}

// Default user preferences
const DEFAULT_USER_PREFERENCES: NavigationUserPreferences = {
  sidebarDefaultOpen: true,
  autoExpandFavorites: true,
  rememberLastSection: true,
  compactMode: false,
  animationSpeed: 'normal'
}

export const useNavigation = () => {
  const context = useContext(NavigationContext)
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider')
  }
  return context
}

interface NavigationProviderProps {
  children: ReactNode
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const { isNewUser } = useAuth()
  
  // Initialize sidebar state from localStorage or user preference
  const [isSidebarOpen, setIsSidebarOpenInternal] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SIDEBAR_OPEN)
      if (stored !== null) {
        return JSON.parse(stored)
      }
      // Fall back to user preference if no stored state
      const prefs = localStorage.getItem(STORAGE_KEYS.USER_PREFERENCES)
      if (prefs) {
        const parsedPrefs = JSON.parse(prefs)
        return parsedPrefs.sidebarDefaultOpen ?? DEFAULT_USER_PREFERENCES.sidebarDefaultOpen
      }
      return DEFAULT_USER_PREFERENCES.sidebarDefaultOpen
    } catch (error) {
      console.warn('NavigationContext: Error initializing sidebar state:', error)
      return DEFAULT_USER_PREFERENCES.sidebarDefaultOpen
    }
  })

  // Initialize expanded sections from localStorage or default
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.EXPANDED_SECTIONS)
      return stored ? JSON.parse(stored) : DEFAULT_EXPANDED_SECTIONS
    } catch {
      return DEFAULT_EXPANDED_SECTIONS
    }
  })

  // Initialize topics sidebar collapse state from localStorage or default to false
  const [isTopicsSidebarCollapsed, setIsTopicsSidebarCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.TOPICS_SIDEBAR_COLLAPSED)
      return stored ? JSON.parse(stored) : false
    } catch {
      return false
    }
  })

  // Initialize user preferences from localStorage or default
  const [userPreferences, setUserPreferences] = useState<NavigationUserPreferences>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.USER_PREFERENCES)
      return stored ? { ...DEFAULT_USER_PREFERENCES, ...JSON.parse(stored) } : DEFAULT_USER_PREFERENCES
    } catch {
      return DEFAULT_USER_PREFERENCES
    }
  })

  // Initialize view-specific states from localStorage or default
  const [viewStates, setViewStates] = useState<Record<string, any>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.VIEW_STATES)
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  })

  // Persist sidebar state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_OPEN, JSON.stringify(isSidebarOpen))
    } catch (error) {
      console.warn('Failed to save sidebar state to localStorage:', error)
    }
  }, [isSidebarOpen])

  // Persist expanded sections to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.EXPANDED_SECTIONS, JSON.stringify(expandedSections))
    } catch (error) {
      console.warn('Failed to save expanded sections to localStorage:', error)
    }
  }, [expandedSections])

  // Persist topics sidebar collapse state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.TOPICS_SIDEBAR_COLLAPSED, JSON.stringify(isTopicsSidebarCollapsed))
    } catch (error) {
      console.warn('Failed to save topics sidebar collapse state to localStorage:', error)
    }
  }, [isTopicsSidebarCollapsed])

  // Persist user preferences to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.USER_PREFERENCES, JSON.stringify(userPreferences))
    } catch (error) {
      console.warn('Failed to save user preferences to localStorage:', error)
    }
  }, [userPreferences])

  // Persist view states to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.VIEW_STATES, JSON.stringify(viewStates))
    } catch (error) {
      console.warn('Failed to save view states to localStorage:', error)
    }
  }, [viewStates])

  // Sync with backend user settings on mount (only for existing users)
  useEffect(() => {
    const syncWithBackendSettings = async () => {
      try {
        // Only sync if user is authenticated and NOT a new user
        const user = auth.currentUser
        if (!user || isNewUser) return

        // Fetch user settings from backend
        const userSettings = await settingsService.getUserSettings()
        
        // Sync sidebar collapsed preference
        if (userSettings?.appearance?.sidebarCollapsed !== undefined) {
          const shouldBeCollapsed = userSettings.appearance.sidebarCollapsed
          // Update navigation preference
          setUserPreferences(prev => ({
            ...prev,
            sidebarDefaultOpen: !shouldBeCollapsed
          }))
          // Don't override current session state, just update the default preference
          // The current session state is already set from localStorage
        }
      } catch (error) {
        // Silently fail - user may not have settings yet or backend may be unavailable
        console.debug('NavigationContext: Could not sync with backend settings:', error)
      }
    }

    syncWithBackendSettings()
  }, [isNewUser]) // Run when isNewUser changes

  // Wrapper function for setIsSidebarOpen
  const setIsSidebarOpen = useCallback((open: boolean) => {
    setIsSidebarOpenInternal(open)
  }, [])

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(!isSidebarOpen)
  }, [isSidebarOpen, setIsSidebarOpen])

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }, [])

  const toggleTopicsSidebar = useCallback(() => setIsTopicsSidebarCollapsed(!isTopicsSidebarCollapsed), [isTopicsSidebarCollapsed])

  const updateUserPreference = useCallback((key: keyof NavigationUserPreferences, value: any) => {
    setUserPreferences(prev => ({
      ...prev,
      [key]: value
    }))
  }, [])

  const setViewState = useCallback((viewId: string, state: any) => {
    setViewStates(prev => ({
      ...prev,
      [viewId]: state
    }))
  }, [])

  const getViewState = useCallback((viewId: string, defaultValue?: any) => {
    return viewStates[viewId] ?? defaultValue
  }, [viewStates])

  const resetAllNavigation = useCallback(() => {
    setIsSidebarOpenInternal(DEFAULT_USER_PREFERENCES.sidebarDefaultOpen)
    setExpandedSections(DEFAULT_EXPANDED_SECTIONS)
    setIsTopicsSidebarCollapsed(false)
    setViewStates({})
    // Note: userPreferences are not reset as they are user-specific settings
  }, [])

  const exportNavigationState = useCallback(() => {
    const exportData = {
      sidebarOpen: isSidebarOpen,
      expandedSections,
      topicsSidebarCollapsed: isTopicsSidebarCollapsed,
      userPreferences,
      viewStates,
      exportDate: new Date().toISOString()
    }
    return JSON.stringify(exportData, null, 2)
  }, [isSidebarOpen, expandedSections, isTopicsSidebarCollapsed, userPreferences, viewStates])

  const importNavigationState = useCallback((stateJson: string): boolean => {
    try {
      const importedData = JSON.parse(stateJson)
      
      if (importedData.sidebarOpen !== undefined) {
        setIsSidebarOpenInternal(importedData.sidebarOpen)
      }
      if (importedData.expandedSections) {
        setExpandedSections(importedData.expandedSections)
      }
      if (importedData.topicsSidebarCollapsed !== undefined) {
        setIsTopicsSidebarCollapsed(importedData.topicsSidebarCollapsed)
      }
      if (importedData.userPreferences) {
        setUserPreferences(importedData.userPreferences)
      }
      if (importedData.viewStates) {
        setViewStates(importedData.viewStates)
      }
      
      return true
    } catch (error) {
      console.error('Failed to import navigation state:', error)
      return false
    }
  }, [])

  return (
    <NavigationContext.Provider value={{
      isSidebarOpen,
      setIsSidebarOpen,
      toggleSidebar,
      expandedSections,
      setExpandedSections,
      toggleSection,
      isTopicsSidebarCollapsed,
      setIsTopicsSidebarCollapsed,
      toggleTopicsSidebar,
      userPreferences,
      updateUserPreference,
      viewStates,
      setViewState,
      getViewState,
      resetAllNavigation,
      exportNavigationState,
      importNavigationState
    }}>
      {children}
    </NavigationContext.Provider>
  )
}
